import React from "react";
import classes from "./assistantDrawer.module.css";

function splitTableRow(line) {
	const trimmed = String(line || "").trim().replace(/^\|/, "").replace(/\|$/, "");
	const cells = [];
	let cell = "";
	let escaped = false;
	let inCode = false;

	for (const character of trimmed) {
		if (escaped) {
			cell += character;
			escaped = false;
			continue;
		}
		if (character === "\\") {
			escaped = true;
			continue;
		}
		if (character === "`") inCode = !inCode;
		if (character === "|" && !inCode) {
			cells.push(cell.trim());
			cell = "";
			continue;
		}
		cell += character;
	}
	cells.push(cell.trim());
	return cells;
}

function isTableDivider(line) {
	const cells = splitTableRow(line);
	return cells.length > 0 && cells.every((cell) => /^:?-{3,}:?$/.test(cell));
}

function tableAlignment(cell) {
	const left = cell.startsWith(":");
	const right = cell.endsWith(":");
	if (left && right) return "center";
	if (right) return "right";
	return "left";
}

function isSafeLink(href) {
	return /^(https?:|mailto:|tel:)/i.test(String(href || "").trim());
}

function renderInline(text, keyPrefix) {
	const source = String(text || "");
	const tokenPattern = /(`[^`\n]+`|\*\*[^*\n]+\*\*|__[^_\n]+__|~~[^~\n]+~~|\[[^\]\n]+\]\([^) \n]+(?:\s+"[^"]*")?\)|\*[^*\n]+\*|_[^_\n]+_)/g;
	const output = [];
	let cursor = 0;
	let match;
	let index = 0;

	while ((match = tokenPattern.exec(source)) !== null) {
		if (match.index > cursor) {
			output.push(source.slice(cursor, match.index));
		}

		const token = match[0];
		const key = `${keyPrefix}-${index}`;
		if (token.startsWith("`")) {
			output.push(<code key={key}>{token.slice(1, -1)}</code>);
		} else if (token.startsWith("**") || token.startsWith("__")) {
			output.push(<strong key={key}>{renderInline(token.slice(2, -2), `${key}-strong`)}</strong>);
		} else if (token.startsWith("~~")) {
			output.push(<del key={key}>{renderInline(token.slice(2, -2), `${key}-del`)}</del>);
		} else if (token.startsWith("[")) {
			const linkMatch = token.match(/^\[([^\]]+)\]\(([^) "\n]+)(?:\s+"([^"]*)")?\)$/);
			const label = linkMatch?.[1] || token;
			const href = linkMatch?.[2] || "";
			if (isSafeLink(href)) {
				output.push(
					<a key={key} href={href} title={linkMatch?.[3]} target="_blank" rel="noreferrer">
						{label}
					</a>
				);
			} else {
				output.push(label);
			}
		} else {
			output.push(<em key={key}>{renderInline(token.slice(1, -1), `${key}-em`)}</em>);
		}

		cursor = tokenPattern.lastIndex;
		index += 1;
	}

	if (cursor < source.length) output.push(source.slice(cursor));
	return output;
}

function startsBlock(lines, index) {
	const line = lines[index] || "";
	const nextLine = lines[index + 1] || "";
	return (
		!line.trim() ||
		/^#{1,6}\s+/.test(line) ||
		/^```/.test(line.trim()) ||
		/^>\s?/.test(line) ||
		/^\s*[-*+]\s+/.test(line) ||
		/^\s*\d+[.)]\s+/.test(line) ||
		/^\s*(?:-{3,}|\*{3,}|_{3,})\s*$/.test(line) ||
		(nextLine && line.includes("|") && isTableDivider(nextLine))
	);
}

function renderTable(lines, startIndex, key) {
	const headers = splitTableRow(lines[startIndex]);
	const divider = splitTableRow(lines[startIndex + 1]);
	const alignments = headers.map((_, index) => tableAlignment(divider[index] || "---"));
	const rows = [];
	let index = startIndex + 2;

	while (index < lines.length && lines[index].trim() && lines[index].includes("|")) {
		const cells = splitTableRow(lines[index]);
		rows.push(headers.map((_, cellIndex) => cells[cellIndex] || ""));
		index += 1;
	}

	return {
		endIndex: index,
		element: (
			<div className={classes.markdownTableWrap} key={key}>
				<table className={classes.markdownTable}>
					<thead>
						<tr>
							{headers.map((header, headerIndex) => (
								<th key={`${key}-header-${headerIndex}`} style={{ textAlign: alignments[headerIndex] }}>
									{renderInline(header, `${key}-header-${headerIndex}`)}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{rows.map((row, rowIndex) => (
							<tr key={`${key}-row-${rowIndex}`}>
								{row.map((cell, cellIndex) => (
									<td key={`${key}-cell-${rowIndex}-${cellIndex}`} style={{ textAlign: alignments[cellIndex] }}>
										{renderInline(cell, `${key}-cell-${rowIndex}-${cellIndex}`)}
									</td>
								))}
							</tr>
						))}
					</tbody>
				</table>
			</div>
		)
	};
}

export default function AssistantMarkdown({ content = "", colorScheme = "light" }) {
	const lines = String(content || "").replace(/\r\n?/g, "\n").split("\n");
	const elements = [];
	let index = 0;

	while (index < lines.length) {
		const line = lines[index];
		const key = `markdown-${index}`;

		if (!line.trim()) {
			index += 1;
			continue;
		}

		if (index + 1 < lines.length && line.includes("|") && isTableDivider(lines[index + 1])) {
			const table = renderTable(lines, index, key);
			elements.push(table.element);
			index = table.endIndex;
			continue;
		}

		const heading = line.match(/^(#{1,6})\s+(.+)$/);
		if (heading) {
			const level = Math.min(heading[1].length + 2, 6);
			const Heading = `h${level}`;
			elements.push(<Heading key={key}>{renderInline(heading[2], key)}</Heading>);
			index += 1;
			continue;
		}

		if (/^```/.test(line.trim())) {
			const language = line.trim().slice(3).trim();
			const code = [];
			index += 1;
			while (index < lines.length && !/^```/.test(lines[index].trim())) {
				code.push(lines[index]);
				index += 1;
			}
			if (index < lines.length) index += 1;
			elements.push(
				<div className={classes.markdownCodeBlock} key={key}>
					{language ? <span className={classes.markdownCodeLanguage}>{language}</span> : null}
					<pre><code>{code.join("\n")}</code></pre>
				</div>
			);
			continue;
		}

		if (/^\s*(?:-{3,}|\*{3,}|_{3,})\s*$/.test(line)) {
			elements.push(<hr key={key} />);
			index += 1;
			continue;
		}

		if (/^>\s?/.test(line)) {
			const quoteLines = [];
			while (index < lines.length && /^>\s?/.test(lines[index])) {
				quoteLines.push(lines[index].replace(/^>\s?/, ""));
				index += 1;
			}
			elements.push(<blockquote key={key}>{renderInline(quoteLines.join(" "), key)}</blockquote>);
			continue;
		}

		const unordered = line.match(/^\s*[-*+]\s+(.+)$/);
		const ordered = line.match(/^\s*\d+[.)]\s+(.+)$/);
		if (unordered || ordered) {
			const orderedList = Boolean(ordered);
			const items = [];
			const itemPattern = orderedList ? /^\s*\d+[.)]\s+(.+)$/ : /^\s*[-*+]\s+(.+)$/;
			while (index < lines.length) {
				const item = lines[index].match(itemPattern);
				if (!item) break;
				items.push(item[1]);
				index += 1;
			}
			const List = orderedList ? "ol" : "ul";
			elements.push(
				<List key={key}>
					{items.map((item, itemIndex) => (
						<li key={`${key}-item-${itemIndex}`}>{renderInline(item, `${key}-item-${itemIndex}`)}</li>
					))}
				</List>
			);
			continue;
		}

		const paragraph = [line.trim()];
		index += 1;
		while (index < lines.length && !startsBlock(lines, index)) {
			paragraph.push(lines[index].trim());
			index += 1;
		}
		elements.push(<p key={key}>{renderInline(paragraph.join(" "), key)}</p>);
	}

	return (
		<div
			className={`${classes.assistantMarkdown} ${
				colorScheme === "dark" ? classes.assistantMarkdownDark : classes.assistantMarkdownLight
			}`}
		>
			{elements}
		</div>
	);
}
